/*
  Utility functions for interacting with SharePoint via Microsoft Graph API.
*/


import { axiosClient } from '@/core/services/http.service';
// MS Graph API base URL.
const spUrl_Base = 'https://graph.microsoft.com/v1.0';

/* ---------------------------------------------------------- */
// SharePoint - Sites.

/**
 * Construct the URL to query the MSG Site Properties.
 * @returns URL to query the MSG Site Properties.
 */
const getSpUrl_Site = (hostName, siteName) => {
  // https://graph.microsoft.com/v1.0/sites/{hostname} (no site name, eg. OneDrive personal files)
  // https://graph.microsoft.com/v1.0/sites/{hostname}:/sites/{site-name}
  let url = `${spUrl_Base}/sites/${hostName}`;
  if (!!siteName) url += `:/sites/${siteName}`;
  return url;
};

/**
 * Get a specific SharePoint site by ID
 * @param {string} siteId - The SharePoint site ID
 * @returns {Object|null} SharePoint Site data or null if not found
 */

export const getSiteById = async (siteId) => {
  if (!siteId) {
    throw new Error('Site ID is required');
  }
  try {
    const response = await axiosClient.get(`${spUrl_Base}/sites/${siteId}`);
    if (response.success && response.body?.id) {
      return response.body;

    } else {
      throw new Error(`Failed to load SharePoint site: ${response.body?.error?.message}` || 'Failed to load SharePoint site. Please check the site ID and your network connection.');
    }
  } catch (error) {
    throw error;
  }
};

/**
 * Get a specific SharePoint site by URL
 * @param {string} siteUrl - The full SharePoint site URL (e.g. https://hostname.sharepoint.com/sites/SiteName)
 * @returns {Object|null} SharePoint Site data or null if not found
 */
export const getSite = async (siteUrl) => {
  if (!siteUrl) {
    throw new Error('SharePoint site URL is required');
  }

  // Remove all whitespace from input (both inside and outside)
  const cleanInput = siteUrl.replace(/\s+/g, '');

  // Validate input is a URL
  if (!cleanInput.startsWith('http://') && !cleanInput.startsWith('https://')) {
    throw new Error('Input must be a valid SharePoint site URL (e.g. https://hostname.sharepoint.com/sites/SiteName)');
  }

  let targetHostName = '';
  let siteName = '';

  try {
    const url = new URL(cleanInput);
    targetHostName = url.hostname;

    // Extract site name from URL path
    const pathParts = url.pathname.split('/').filter(part => part.length > 0);
    if (pathParts.length >= 2 && pathParts[0] === 'sites') {
      siteName = pathParts[1];
    } else {
      throw new Error('Invalid SharePoint URL format. Expected format: https://hostname.sharepoint.com/sites/SiteName');
    }
  } catch (urlError) {
    throw new Error('Invalid SharePoint site URL. Please check the format.');
  }

  if (!targetHostName) {
    throw new Error('No SharePoint hostname found in the URL.');
  }

  if (!siteName) {
    throw new Error('Site name could not be determined from the URL.');
  }

  try {
    const response = await axiosClient.get(getSpUrl_Site(targetHostName, siteName));
    if (response.success && response.body?.id) {
      return response.body;
    } else {
      throw new Error(`Failed to load SharePoint site "${siteName}": ${response.body?.error?.message}` || `Failed to load SharePoint site "${siteName}". Please check the site URL and your network connection.`);
    }
  } catch (error) {
    throw error;
  }
};

/* ---------------------------------------------------------- */
// Site Drives/Libraries

/** Site Drives/Libraries URL. */
const getSpUrl_Drive_List = (siteId) => {
  // https://graph.microsoft.com/v1.0/sites/{site-id}/drives
  return `${spUrl_Base}/sites/${siteId}/drives`;
};

/** * Get all drives/document libraries for a specific SharePoint site.
 * @param {string} siteId - The SharePoint site ID
 * @returns {Promise<Object[]>} A promise that resolves to an array of drives
 * */
export const getSiteDrives = async (siteId) => {
  const drives = {};
  if (siteId) {
    try {
      // Get all drives/document libraries for the site
      const response = await axiosClient.get(getSpUrl_Drive_List(siteId));
      
      if (response.success && response.body?.value) {
        // Add all drives to the collection
        for (const drive of response.body.value) {
          if (drive.id) {
            drives[drive.id] = drive;
          }
        }
      } else {
        const errorMessage = response.body?.error?.message || null;
        throw new Error(`Failed to fetch drives: ${errorMessage}`);
      }
    } catch (error) {
      throw error;
    }
  }
  return Object.values(drives);
};

/* ---------------------------------------------------------- */
// Site Items - Folders, Files etc.

/** Construct the URL to query SharePoint items (folders, files) in a drive. */
const getSpUrl_Items = (driveId, itemId) => {
  // https://graph.microsoft.com/v1.0/drives/{drive-id}/root/children (to list root items)
  // https://graph.microsoft.com/v1.0/drives/{drive-id}/items/{item-id}/children (to list folder contents)
  let url = `${spUrl_Base}/drives/${driveId}`;
  url += (!itemId) ? '/root/children' : `/items/${itemId}/children`;
  return url;
};

export const getItems = async (driveId, parentId) => {
  const items = [];
  if (!!driveId) {
    try {
      const response = await axiosClient.get(getSpUrl_Items(driveId, parentId));

      if (response?.body?.value) {
        items.push(response.body.value);
      } else {
        const errorMessage = response.body?.error?.message || null;
        throw new Error(`Failed to fetch items: ${errorMessage}`);
      }
    } catch (error) {
      throw error;
    }
  }
  return items;
};

/* ---------------------------------------------------------- */
// Construct SharePoint Display Path

/**
 * Constructs a display path from SharePoint location parameters in format 'SiteName / DriveName / FolderName'.
 * @param {string} SPDefaultSiteId - The SharePoint site ID
 * @param {string} [SPDefaultDriveId] - The SharePoint drive ID (optional)
 * @param {string} [SPDefaultFolderId] - The SharePoint folder ID (optional)
 * @param {string} [SPDefaultFolderPath] - The folder path (optional)
 * @returns {Promise<string>} The constructed display path
 */
export const constructSharePointDisplayPath = async (SPDefaultSiteId, SPDefaultDriveId, SPDefaultFolderId, SPDefaultFolderPath) => {
  const pathArray = [];

  if (!SPDefaultSiteId) {
    return '';
  }

  try {
    // Get site information
    const siteResponse = await axiosClient.get(`${spUrl_Base}/sites/${SPDefaultSiteId}`);
    if (siteResponse?.body?.displayName) {
      pathArray.push(siteResponse.body.displayName);
    } else {
      throw new Error(`Failed to fetch default site: ${siteResponse.body?.error?.message || 'Unknown error'}`);
    }

    // Get drive information if SPDefaultDriveId exists
    if (SPDefaultDriveId) {
      const driveResponse = await axiosClient.get(`${spUrl_Base}/drives/${SPDefaultDriveId}`);
      if (driveResponse?.body?.name) {
        pathArray.push(driveResponse.body.name);
      } else {
        throw new Error(`Failed to fetch drive: ${driveResponse.body?.error?.message || 'Unknown error'}`);
      }
    }

    // Get folder information if SPDefaultFolderId exists
    if (SPDefaultFolderId && SPDefaultFolderPath) {
      // Get the part after "root:/" and split by "/"
      const rootIndex = SPDefaultFolderPath.indexOf('root:/');
      if (rootIndex !== -1) {
        const pathAfterRoot = SPDefaultFolderPath.substring(rootIndex + 6); // 6 = length of "root:/"
        const pathSegments = pathAfterRoot.split('/').filter(segment => segment.trim() !== '');
        
        // Add path segments to the array
        pathArray.push(...pathSegments);
      }
      
      // Get the specific folder name using the folder ID
      const folderResponse = await axiosClient.get(`${spUrl_Base}/drives/${SPDefaultDriveId}/items/${SPDefaultFolderId}`);
      if (folderResponse?.body?.name) {
        pathArray.push(folderResponse.body.name);
      } else {
        throw new Error(`Failed to fetch folder: ${folderResponse.body?.error?.message || 'Unknown error'}`);
      }
    }
    // Join the array with '/' to create the display path
    return pathArray.join(' / ');

  } catch (error) {
    throw error;
  }
};

/* ---------------------------------------------------------- */
// Site-wide Search

/**
 * Search for files across multiple drives in a SharePoint site
 * @param {string} siteId - The SharePoint site ID
 * @param {string} searchQuery - The search query string
 * @returns {Promise<Object[]>} A promise that resolves to an array of search results from all drives
 */
export const searchSite = async (siteId, searchQuery) => {
  if (!siteId || !searchQuery) {
    return [];
  }

  try {
    // First get all drives in the site
    const drives = await getSiteDrives(siteId);
    
    if (!drives || drives.length === 0) {
      return [];
    }
    
    // Search each drive with concurrency limit and collect results
    const CONCURRENT_SEARCHES = 3; // Limit concurrent API calls
    const searchResults = [];
    
    for (let i = 0; i < drives.length; i += CONCURRENT_SEARCHES) {
      const driveBatch = drives.slice(i, i + CONCURRENT_SEARCHES);
      
      const batchPromises = driveBatch.map(async (drive) => {
        try {
          const driveSearchUrl = `${spUrl_Base}/drives/${drive.id}/search(q='${encodeURIComponent(searchQuery)}')`;
          const response = await axiosClient.get(driveSearchUrl);
          
          if (response.success && response.body?.value && Array.isArray(response.body.value)) {
            return response.body.value
              .filter(item => item && (item.name || item.displayName)) // Ensure valid items
              .map(item => ({
                ...item,
                type: item.folder ? 'folder' : 'file',
                driveId: drive.id,
                driveName: drive.name,
                folderId: item.parentReference?.id,
                siteId: siteId,
                // Normalise file names
                displayName: item.displayName || item.name,
                name: item.name || item.displayName,
              }));
          }
          return [];
        } catch (error) {
          // Log individual drive search failures but continue with other drives
          // console.warn(`Search failed for drive ${drive.name}:`, error.message);
          return [];
        }
      });

      // Wait for this batch to complete
      const batchResults = await Promise.all(batchPromises);
      searchResults.push(...batchResults.flat());
    }

    // Remove duplicates based on file ID and sort by relevance (name match first)
    const uniqueResults = Array.from(
      new Map(searchResults.map(item => [item.id, item])).values(),
    );

    const searchLower = searchQuery.toLowerCase();

    // Filter results based on file name only
    const filteredResults = uniqueResults.filter(item => {
      return item.name.toLowerCase().includes(searchLower) || item.displayName.toLowerCase().includes(searchLower);
    });

    // Sort results by name
    const sortedResults = filteredResults.sort((a, b) =>
      a.name.localeCompare(b.name),
    );
    return sortedResults;

  } catch (error) {
    throw new Error(`Site search failed: ${error.message}`);
  }
};

/* ---------------------------------------------------------- */
// File Download

/**
 * Get the direct download URL for a file in SharePoint using Microsoft Graph API.
 * @param {string} driveId - The ID of the drive.
 * @param {string} itemId - The ID of the file item.
 * @returns {Promise<string|null>} The download URL or null if parameters are missing.
 */
export const getFileDownloadUrl = async (driveId, itemId) => {
  if (!driveId || !itemId) return null;
  const url = `${spUrl_Base}/drives/${driveId}/items/${itemId}`;
  const response = await axiosClient.get(url, {});
  
  if (response.body['@microsoft.graph.downloadUrl']) {
    return response.body['@microsoft.graph.downloadUrl'];
  } 
  throw new Error(`Failed to get download URL for item: ${response.body?.error?.message || 'Unknown error'}`);
};

/**
 * Fetch the file as a Blob and return a Blob URL for it.
 * @param {string} downloadUrl - The direct download URL for the file.
 * @returns {Promise<string|null>} The Blob URL or null if failed.
 */
export const getBlobUrl = async (downloadUrl) => {
  if (!downloadUrl) return null;
  const blobResponse = await axiosClient.get(downloadUrl, {
    responseType: 'blob',
  });
  return URL.createObjectURL(blobResponse.body);
};

/* ---------------------------------------------------------- */